import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { SharedModule, TypedBaseListComponent, DataTableFilterCollection, ModalService, NotificationService, DataService, ChannelService, Permission } from '@vendure/admin-ui/core';
import { GetAllStoreSitesDocument, CREATE_STORE_SITE, ARCHIVE_STORE_SITE_DOCUMENT, GET_ACTIVE_CHANNEL_WITH_SELLER_CUSTOM_FIELDS, GET_SELLER_OWNER_ADMIN_USER_ID } from '../../common/ui-types';
import { VariablesOf } from '@graphql-typed-document-node/core';
import { StoreSiteListOptions, StoreSiteFilterInput, Mutation, StoreSite } from '../../../common/ui/generated';
import { CreateStoreSiteDialogComponent } from '../create-store-site-dialog/create-store-site-dialog.component';
import { ArchiveStoreSiteDialogComponent } from '../archive-store-site-dialog/archive-store-site-dialog.component';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, finalize, map, mergeMap, Observable, of, shareReplay, switchMap, take, catchError } from 'rxjs';



@Component({
    selector: 'store-sites-list',
    templateUrl: './store-sites-list.component.html',
    styleUrls: ['./store-sites-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],//, StarRatingComponent, ReviewStateLabelComponent],
})
export class StoreSitesListComponent extends TypedBaseListComponent<
    typeof GetAllStoreSitesDocument,
    'storeSites'
> {
    filteredState: string | null = 'new';
    isCreatingStoreSite = false;

    private readonly sellerId$ = new BehaviorSubject<string | null>(null);

    @Input()
    set sellerId(value: string | null) {
        this.sellerId$.next(value);
    }
    get sellerId(): string | null {
        return this.sellerId$.value;
    }

    /**
     * Create button is visible/enabled if the current user is:
     * - the seller owner admin (seller.customFields.ownerAdminUserId), OR
     * - a SuperAdmin.
     */
    readonly canCreateStoreSite$: Observable<boolean>;

    // Here we set up the filters that will be available
    // to use in the data table
    readonly filters: DataTableFilterCollection<StoreSiteFilterInput> = this.createFilterCollection()
        .addDateFilters();

    // Here we set up the sorting options that will be available
    // to use in the data table
    readonly sorts = this.createSortCollection()
        .defaultSort('id', 'DESC')
        .addSort({ name: 'id' })
        .addSort({ name: 'createdAt' })
        .addSort({ name: 'updatedAt' })
        .addSort({ name: 'sellerId' })
        .addSort({ name: 'name' })
        .addSort({ name: 'identifier' })
        .connectToRoute(this.route);

    constructor(
        private modalService: ModalService,
        private notificationService: NotificationService,
        protected router: Router,
        protected dataService: DataService,
        private channelService: ChannelService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
        super.configure({
            document: GetAllStoreSitesDocument,
            getItems: (data: any) => data.storeSites,
            setVariables: ((skip, take) => ({
                options: this.createQueryOptions(skip, take, this.searchTermControl.value),
            })) as (skip: number, take: number) => VariablesOf<typeof GetAllStoreSitesDocument>,
            refreshListOnChanges: [this.filters.valueChanges, this.sorts.valueChanges],
        });

        const currentUser$ = this.dataService.auth.currentUser().single$.pipe(
            map(({ me }) => me ?? null),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        const userIsSuperAdmin$ = currentUser$.pipe(
            map(me => me?.channels.some(channel => channel.permissions.includes(Permission.SuperAdmin)) ?? false),
            distinctUntilChanged(),
        );

        const ownerAdminUserId$ = this.sellerId$.pipe(
            distinctUntilChanged(),
            switchMap(sellerId => {
                if (sellerId) {
                    return this.dataService
                        .query(GET_SELLER_OWNER_ADMIN_USER_ID as any, { id: sellerId })
                        .mapSingle((data: any) => data?.seller?.customFields?.ownerAdminUserId ?? null)
                        .pipe(catchError(() => of(null)));
                }

                // No explicit sellerId: use the selected active channel's seller customFields (non-default channels).
                return this.dataService
                    .query(GET_ACTIVE_CHANNEL_WITH_SELLER_CUSTOM_FIELDS as any, {})
                    .mapSingle((data: any) => data?.activeChannel?.seller?.customFields?.ownerAdminUserId ?? null)
                    .pipe(catchError(() => of(null)));
            }),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        const userIsOwnerAdmin$ = combineLatest([currentUser$, ownerAdminUserId$]).pipe(
            map(([me, ownerAdminUserId]) => !!me?.id && !!ownerAdminUserId && me.id === ownerAdminUserId),
            distinctUntilChanged(),
        );

        this.canCreateStoreSite$ = combineLatest([userIsSuperAdmin$, userIsOwnerAdmin$]).pipe(
            map(([isSuperAdmin, isOwnerAdmin]) => isSuperAdmin || isOwnerAdmin),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    openCreateDialog() {
        this.canCreateStoreSite$.pipe(take(1)).subscribe(canCreate => {
            if (!canCreate) {
                this.notificationService.error('You do not have permission to create a store site');
                return;
            }
            this.dataService.auth.currentUser().single$.pipe(take(1)).subscribe(({ me }) => {
                const userIsSuperAdmin = me?.channels.some(channel => channel.permissions.includes(Permission.SuperAdmin)) ?? false;
                this.modalService
                    .fromComponent(CreateStoreSiteDialogComponent, {
                        closable: true,
                        size: 'lg',
                        locals: {
                            userIsSuperAdmin,
                        },
                    })
                    .subscribe((result) => {
                        if (result) {
                            this.isCreatingStoreSite = true;
                            this.cdr.markForCheck();
                            const createInput: any = {
                                sellerId: this.sellerId,
                                name: result.name,
                                isDemo: !!(result as any).isDemo,
                                demoExpiresAt: (result as any).demoExpiresAt,
                                demoCustomerName: (result as any).demoCustomerName,
                                demoCustomerEmail: (result as any).demoCustomerEmail,
                                // These fields are only relevant when duplicating from an existing store site.
                                sourceStoreSiteId: (result as any).sourceStoreSiteId,
                                options: (result as any).options,
                            };
                            if (result.identifier) {
                                createInput.identifier = result.identifier;
                            }
                            this.dataService.mutate(CREATE_STORE_SITE, {
                                input: createInput,
                            }).pipe(
                                mergeMap(({ createStoreSite }: any) =>
                                    this.dataService.auth.currentUser().single$.pipe(
                                        map(({ me }) => ({
                                            me,
                                            createStoreSite
                                        })),
                                    ),
                                ),
                                mergeMap(({ me, createStoreSite }: any) =>
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    this.dataService.client.updateUserChannels(me!.channels).pipe(map(() => createStoreSite)),
                                ),
                                finalize(() => {
                                    this.isCreatingStoreSite = false;
                                    this.cdr.markForCheck();
                                }),
                            ).subscribe({
                                next: () => {
                                    this.notificationService.success('Store site created successfully');
                                    this.refresh();
                                },
                                error: (err) => {
                                    this.notificationService.error('Failed to create store site: ' + (err?.message ?? err));
                                },
                            });
                        }
                    });
            });
        });
    }

    async openDetailPage(storeSite: StoreSite) {
        this.router.navigate(['../../', 'store-settings', 'site-component'], { relativeTo: this.route });
    }

    /**
     * Open dialog in "duplicate" mode for the given store site.
     * This will allow the user to customize the new store and optionally copy pages.
     */
    async openDuplicateDialog(storeSite: StoreSite) {
        this.dataService.auth.currentUser().single$.pipe(take(1)).subscribe(({ me }) => {
            const userIsSuperAdmin = me?.channels.some(channel => channel.permissions.includes(Permission.SuperAdmin)) ?? false;
            this.modalService
                .fromComponent(CreateStoreSiteDialogComponent, {
                    closable: true,
                    size: 'lg',
                    locals: {
                        existingStoreSite: storeSite,
                        userIsSuperAdmin,
                    },
                })
                .subscribe((result) => {
                    if (result) {
                        this.isCreatingStoreSite = true;
                        this.cdr.markForCheck();
                        const createInput: any = {
                            sellerId: this.sellerId,
                            name: result.name,
                            isDemo: !!(result as any).isDemo,
                            demoExpiresAt: (result as any).demoExpiresAt,
                            demoCustomerName: (result as any).demoCustomerName,
                            demoCustomerEmail: (result as any).demoCustomerEmail,
                            sourceStoreSiteId: (result as any).sourceStoreSiteId,
                            options: (result as any).options,
                        };
                        if (result.identifier) {
                            createInput.identifier = result.identifier;
                        }
                        this.dataService.mutate(CREATE_STORE_SITE, {
                            input: createInput,
                        }).pipe(
                            mergeMap(({ createStoreSite }: any) =>
                                this.dataService.auth.currentUser().single$.pipe(
                                    map(({ me }) => ({
                                        me,
                                        createStoreSite
                                    })),
                                ),
                            ),
                            mergeMap(({ me, createStoreSite }: any) =>
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                this.dataService.client.updateUserChannels(me!.channels).pipe(map(() => createStoreSite)),
                            ),
                            finalize(() => {
                                this.isCreatingStoreSite = false;
                                this.cdr.markForCheck();
                            }),
                        ).subscribe({
                            next: () => {
                                this.notificationService.success('Store site duplicated successfully');
                                this.refresh();
                            },
                            error: (err) => {
                                this.notificationService.error('Failed to duplicate store site: ' + (err?.message ?? err));
                            },
                        });
                    }
                });
        });
    }

    /**
     * Ask for confirmation before archiving a store site.
     * This is a destructive operation and should also cancel the associated subscription.
     */
    confirmArchiveStoreSite(storeSite: any) {
        const archivedChannelId: string | undefined = storeSite?.sellerChannel?.id;
        this.modalService
            .fromComponent(ArchiveStoreSiteDialogComponent, {
                closable: true,
                size: 'md',
                locals: {
                    storeSite: storeSite,
                },
            })
            .subscribe(result => {
                if (!result?.confirmed) {
                    return;
                }
                this.dataService.mutate(ARCHIVE_STORE_SITE_DOCUMENT, {
                    id: storeSite.id,
                }).subscribe({
                    next: () => {
                        this.notificationService.success('Store site archived successfully');

                        // If the archived store site's channel is currently active,
                        // switch the active channel to another available one.
                        if (archivedChannelId) {
                            this.dataService.client
                                .userStatus()
                                .mapSingle(data => data.userStatus)
                                .subscribe(userStatus => {
                                    if (!userStatus || userStatus.activeChannelId !== archivedChannelId) {
                                        this.refresh();
                                        return;
                                    }

                                    const updatedChannels = userStatus.channels.filter(ch => ch.id !== archivedChannelId);
                                    if(updatedChannels.length === 0) {
                                        this.refresh();
                                        return;
                                    }
                                    console.log('updatedChannels', updatedChannels);
                                    this.dataService.client
                                        .updateUserChannels(updatedChannels)
                                        .pipe(
                                            mergeMap(() => this.channelService.setActiveChannel(updatedChannels[0].id))
                                        )
                                        .subscribe({
                                            next: () => this.refresh(),
                                            error: err => {
                                                this.notificationService.error('Failed to switch active channel: ' + err.message);
                                                this.refresh();
                                            },
                                        });
                                });
                        } else {
                            this.refresh();
                        }
                    },
                    error: (err) => {
                        this.notificationService.error('Failed to archive store site: ' + err.message);
                    }
                });
            });
    }

    switchStoreSite(event: Event, storeSiteId: string, channelId: string | undefined) {
        event.preventDefault();

        this.dataService.auth.currentUser().single$.pipe(
            map(({ me }) => me?.channels.some(channel => channel.permissions.includes(Permission.SuperAdmin)) ?? false),
        ).subscribe((userIsSuperAdmin) => {
            if (!channelId) {
                // If no channel, just navigate
                if (userIsSuperAdmin) {
                    this.router.navigate(['./', storeSiteId], { relativeTo: this.route });
                    return;
                }
                // Nothing to do if no channel and user is not super admin
                return;
            }

            // Set active channel first, then navigate after it completes
            this.channelService.setActiveChannel(channelId).subscribe({
                next: () => {
                    // Channel switch successful, now navigate
                    if (userIsSuperAdmin) {
                        this.router.navigate(['../../', 'store-settings', 'general'], { relativeTo: this.route });
                        return;
                    }
                    this.router.navigate(['/'], { relativeTo: this.route });
                },
                error: (err) => {
                    this.notificationService.error('Failed to switch to the store site: ' + err.message);
                    // Nothing to do if store site switch fails
                }
            });
        });
    }

    private createQueryOptions(
        skip: number,
        take: number,
        searchTerm: string | null,
    ): StoreSiteListOptions {
        const filter: any = this.filters.createFilterInput() || {};
        const sort = this.sorts.createSortInput();

        // Add search term filter
        if (searchTerm) {
            filter.name = { contains: searchTerm };
        }

        // Add seller filter if provided
        if (this.sellerId) {
            filter.sellerId = this.sellerId;
        }

        return {
            skip,
            take,
            filter,
            sort,
        };
    }

    // Check if the user can archive the store site.
    // The user can archive the store site if:
    // - there is more than one store site
    // - and the store site is not archived
    canArchive(storeSite: StoreSite): Observable<boolean> {
        return this.items$.pipe(
            map((items: any) => Array.isArray(items) && items.length > 1 && storeSite.status?.toLowerCase() !== 'archived'),
        );
    }

    getStatusLabel(storeSite: StoreSite): string {
        if (storeSite.sellerChannel == null || storeSite.seller == null) {
            return 'archived';
        }
        return storeSite.status ?? 'new';
    }

    getStatusColor(storeSite: StoreSite): 'success' | 'warning' | 'error' | 'default' {
        const label = this.getStatusLabel(storeSite);
        switch (label) {
            case 'published':
                return 'success';
            case 'review':
            case 'preview':
                return 'warning';
            case 'closed':
            case 'archived':
                return 'error';
            default:
                return 'default';
        }
    }
}
